import { useState } from "react";
import { AlertCircle, Check, Eye, EyeOff, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";

const base = "h-11 w-full rounded-lg border bg-white px-3.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] focus:border-cyan-600 focus:ring-4 focus:ring-cyan-500/10";
export const inputCls = (err) => `${base} ${err ? "border-rose-400 focus:border-rose-500 focus:ring-rose-500/10" : "border-slate-200 hover:border-slate-300"}`;

export const FieldError = ({ id, msg }) => msg ? <p id={`${id}-error`} data-testid={`${id}-error`} className="mt-1.5 flex items-center gap-1 text-xs text-rose-600"><AlertCircle className="h-3 w-3" />{msg}</p> : null;

export function Field({ id, label, error, hint, right, ...p }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between"><Label htmlFor={id} className="text-[13px] font-medium text-slate-700">{label}</Label>{right}</div>
      <input id={id} data-testid={`${id}-input`} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined} className={inputCls(error)} {...p} />
      {hint && !error && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
      <FieldError id={id} msg={error} />
    </div>
  );
}

export function PasswordField({ id, label, error, right, ...p }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between"><Label htmlFor={id} className="text-[13px] font-medium text-slate-700">{label}</Label>{right}</div>
      <div className="relative">
        <input id={id} type={show ? "text" : "password"} data-testid={`${id}-input`} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined} className={`${inputCls(error)} pr-11`} {...p} />
        <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"} data-testid={`${id}-toggle`}
          className="absolute inset-y-0 right-0 grid w-11 place-items-center rounded-r-lg text-slate-400 transition-colors hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      <FieldError id={id} msg={error} />
    </div>
  );
}

const RULES = [["8+ characters", (v) => v.length >= 8], ["Upper & lower case", (v) => /[a-z]/.test(v) && /[A-Z]/.test(v)], ["A number", (v) => /\d/.test(v)], ["A symbol", (v) => /[^A-Za-z0-9]/.test(v)]];
const LEVELS = [["Too weak", "bg-rose-500", "text-rose-600"], ["Weak", "bg-rose-500", "text-rose-600"], ["Fair", "bg-amber-500", "text-amber-600"], ["Good", "bg-cyan-600", "text-cyan-700"], ["Strong", "bg-emerald-500", "text-emerald-600"]];
export const pwMeetsMin = (v) => v.length >= 8 && /[A-Za-z]/.test(v) && /\d/.test(v);

export function StrengthMeter({ value }) {
  if (!value) return null;
  const score = RULES.filter(([, f]) => f(value)).length;
  const [label, bar, text] = LEVELS[score];
  return (
    <div className="mt-2" data-testid="password-strength">
      <div className="flex gap-1">{[1, 2, 3, 4].map((i) => <span key={i} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${i <= score ? bar : "bg-slate-200"}`} />)}</div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        <span className={`font-semibold ${text}`} data-testid="password-strength-label">{label}</span>
        {RULES.map(([l, f]) => <span key={l} className={`inline-flex items-center gap-1 ${f(value) ? "text-emerald-600" : "text-slate-400"}`}><Check className="h-3 w-3" />{l}</span>)}
      </div>
    </div>
  );
}

export function SubmitButton({ busy, children, busyText, testid, disabled }) {
  return (
    <button type="submit" disabled={busy || disabled} data-testid={testid} aria-busy={busy}
      className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#0a1f4d] text-sm font-semibold text-white shadow-sm shadow-[#0a1f4d]/20 transition-[background-color,transform,box-shadow] hover:bg-[#12306e] hover:shadow-md active:scale-[.99] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-500/25 disabled:cursor-not-allowed disabled:opacity-60">
      {busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? busyText : children}
    </button>
  );
}

export const Alert = ({ tone = "error", testid, children }) => (
  <div role={tone === "error" ? "alert" : "status"} data-testid={testid}
    className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-[13px] ${tone === "error" ? "border-rose-200 bg-rose-50 text-rose-700" : tone === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-cyan-200 bg-cyan-50 text-cyan-900"}`}>
    {tone === "success" ? <Check className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />}<div>{children}</div>
  </div>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-[18px] w-[18px]" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

export function GoogleButton({ remember, label = "Continue with Google" }) {
  const [busy, setBusy] = useState(false);
  const go = () => {
    setBusy(true);
    localStorage.setItem("dpdp.remember", remember ? "1" : "0");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} disabled={busy} data-testid="google-signin-button"
      className="inline-flex h-11 w-full items-center justify-center gap-2.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 transition-[background-color,border-color] hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-500/20 disabled:opacity-60">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleIcon />}{label}
    </button>
  );
}

export const Divider = ({ text = "or" }) => (
  <div className="my-5 flex items-center gap-3 text-[11px] font-medium uppercase tracking-wider text-slate-400"><span className="h-px flex-1 bg-slate-200" />{text}<span className="h-px flex-1 bg-slate-200" /></div>
);

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
